import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { AiOutlineMinus, AiOutlinePlus } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setUserData } from "../Redux/Actions/Actions";
import { toast } from "react-toastify";
import { HandlePostBasic } from "../utils/Api";
import moment from "moment";
import DatePicker from "react-date-picker";
import "react-date-picker/dist/DatePicker.css";
import "react-calendar/dist/Calendar.css";
import PatientConsent from "../components/Consent";
import { convertBooleanToInt } from "../utils/utils";
import DynamicModal from "../components/DynamicModal";

const OtherDetailsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const UserData = useSelector((state) => state.User);
  const Basic = useSelector((state) => state.Basic);

  const [isProcessing, setIsProcessing] = useState(false);
  const [sliderValue, setSliderValue] = useState("");
  const [gender, setGender] = useState("");
  const [weight, setWeight] = useState("");
  const [age, setAge] = useState("");
  const [isPremature, setIsPremature] = useState(false);
  const [consent, setConsent] = useState(false);
  const [modal, setModal] = useState({
    show: false,
    type: "warning",
    title: "",
    msg: "",
  });

  const [consentData, setConsentData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobileCode: "+91",
    mobileNumber: "",
    relation: "Parent",
  });

  const initialized = useRef(false);

  // Memoized Age Check
  const isUnder18 = useMemo(() => {
    if (!age) return true;
    return moment().diff(moment(age), "years", true) <= 18;
  }, [age]);

  // Sync initial data from Redux
  useEffect(() => {
    const hasUserData =
      UserData && UserData.null !== "null" && Object.keys(UserData).length > 0;
    if (!initialized.current && hasUserData) {
      initialized.current = true;
      setGender(UserData.gender || "");
      setWeight(UserData.weight || "");
      setSliderValue(UserData.height || "");
      
      let parsedDate = "";
      if (UserData.dob) {
        const d = new Date(UserData.dob);
        if (d instanceof Date && !isNaN(d.getTime())) {
          // Self-heal getYear() bug (e.g. year 106 -> 2006)
          if (d.getFullYear() < 1900) {
            d.setFullYear(d.getFullYear() + 1900);
          }
          parsedDate = d;
        }
      }
      setAge(parsedDate);
      setIsPremature(!!UserData.isPremature);
    }
  }, [UserData]);

  const handleContinue = async () => {
    // Validation Logic
    if (
      !sliderValue ||
      Number(sliderValue) === 0 ||
      !gender ||
      !weight ||
      Number(weight) === 0 ||
      (!age && !isPremature)
    ) {
      setModal({
        show: true,
        type: "warning",
        title: "Incomplete Details",
        msg: "Please fill all the details to proceed.",
      });
      return;
    }

    if (!consent) {
      setModal({
        show: true,
        type: "warning",
        title: "Consent Required",
        msg: "Please accept the consent to proceed.",
      });
      return;
    }

    if (isUnder18) {
      const { firstName, lastName, email, mobileNumber } = consentData;
      if (!firstName || !lastName || !email || !mobileNumber) {
        setModal({
          show: true,
          type: "warning",
          title: "Incomplete Details",
          msg: "Please fill consent form details",
        });
        return;
      }
    }

    setIsProcessing(true);

    const headers = {
      "Content-Type": "application/json",
      device: "Android",
      Accept: "application/json",
      CorrelationId: 1234551516516565,
      AuthToken: Basic.authToken,
      userId: Basic.id,
    };

    // Construct payload without mutating Redux state
    const payload = {
      ...UserData,
      gender,
      height: Number(sliderValue),
      weight: Number(weight),
      dob: moment(age).format("YYYY-MM-DD"),
      isPremature,
      creatingFrom: "Android--App--Se--Hai--Ye--",
      Source: "Self Registered",
      contactReference: [consentData],
      consent: convertBooleanToInt(consent),
    };

    try {
      const res = await HandlePostBasic(headers, payload);
      const d = await res.json();
      setIsProcessing(false);

      if (d.status === true) {
        const authData = {
          isExisted: true,
          id: d.userID,
          name: d.firstName,
          type: d.type || d.ype, // Fixed the 'ype' typo from your original code
          authToken: d.authToken,
          rid: null,
          qs: "",
          ConnectionID: d.userID,
        };

        window.ReactNativeWebView?.postMessage(
          JSON.stringify({ type: "LOGIN_SUCESS", data: authData }),
        );
      } else {
        toast.error(d.message || "Update failed");
      }
    } catch (error) {
      setIsProcessing(false);
      setModal({
        show: true,
        type: "error",
        title: "Error",
        msg: "Network error. Please check your connection.",
      });
    }
  };

  // Timeout for processing state
  useEffect(() => {
    let timer;
    if (isProcessing) {
      timer = setTimeout(() => {
        setIsProcessing(false);
        setModal({
          show: true,
          type: "warning",
          title: "Processing Timeout",
          msg: "Something went wrong. Please contact support.",
        });
      }, 8000);
    }
    return () => clearTimeout(timer);
  }, [isProcessing]);

  const updateWeight = (val) => {
    const cleanVal = val.replace(/[^0-9.]/g, "");
    const parts = cleanVal.split(".");
    if (parts.length > 2) return; // Prevent multiple decimals
    setWeight(cleanVal);
  };

  const incrementWeight = () => {
    const current = parseFloat(weight) || 0;
    setWeight((current + 1).toFixed(1).replace(/\.0$/, ""));
  };

  const decrementWeight = () => {
    const current = parseFloat(weight) || 0;
    if (current > 0) {
      setWeight(
        Math.max(0, current - 1)
          .toFixed(1)
          .replace(/\.0$/, ""),
      );
    }
  };

  return (
    <div className="container h-screen max-w-md mx-auto p-8 text-thtext overflow-y-auto relative">
      <DynamicModal
        isOpen={modal.show}
        onClose={() => setModal((prev) => ({ ...prev, show: false }))}
        type={modal.type}
        title={modal.title}
        message={modal.msg}
      />

      <h1 className="text-2xl font-bold mb-4">
        What is your patient's gender, height and weight?
      </h1>

      <div className="flex flex-col space-y-6 mb-6">
        {/* Date of Birth Section */}
        <div>
          <h2 className="mb-2">Date of birth</h2>
          <DatePicker
            format="dd/MM/yyyy"
            minDate={new Date("1900-01-01")}
            maxDate={new Date()}
            defaultActiveStartDate={new Date()}
            value={age}
            onChange={setAge}
            className="w-full bg-transparent rounded-2xl"
          />
          {isUnder18 && (
            <div className="flex items-center space-x-2 mt-2">
              <input
                type="checkbox"
                checked={isPremature}
                onChange={() => setIsPremature(!isPremature)}
              />
              <span className="text-sm">Born at less than 35 weeks?</span>
            </div>
          )}
        </div>

        {/* Gender Selection */}
        <div className="grid grid-cols-2 gap-4">
          {["Male", "Female"].map((g) => (
            <div
              key={g}
              onClick={() => setGender(g)}
              className={`${gender === g ? "bg-accent border-primary" : "bg-gray-100"} 
                border flex flex-col items-center justify-center h-36 rounded-2xl cursor-pointer transition-all`}
            >
              <img
                src={
                  isUnder18
                    ? `./assets/${g.toLowerCase()}1.svg`
                    : `./assets/${g.toLowerCase()}.png`
                }
                className="w-16 h-16 mb-2"
                alt={g}
              />
              <span className="font-bold">{g}</span>
            </div>
          ))}
        </div>

        {/* Height Slider */}
        <div className="flex flex-col items-center">
          <h1 className="text-sm">HEIGHT</h1>
          <p className="text-2xl font-bold text-primary">{sliderValue} cms</p>
          <input
            type="number"
            min="0"
            max="250"
            inputmode="decimal"
            placeholder="Enter height in cm"
            value={sliderValue}
            onChange={(e) => setSliderValue(e.target.value)}
            className="w-full h-12 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary text-center"
          />
        </div>

        {/* Weight Counter */}
        <div className="flex flex-col items-center">
          <h1 className="text-sm">WEIGHT</h1>
          <div className="p-4 bg-gray-100 border border-gray-200 rounded-2xl mt-2 w-48 shadow-sm">
            <div className="bg-white rounded-lg p-2 flex items-center justify-center shadow-inner">
              <input
                type="number"
                inputmode="decimal"
                value={weight}
                onChange={(e) => updateWeight(e.target.value)}
                className="w-16 text-center text-2xl font-bold outline-none bg-transparent"
              />
              <span className="text-sm text-gray-500">kgs</span>
            </div>
            <div className="flex justify-between mt-3 px-2">
              <button
                onClick={decrementWeight}
                className="rounded-full bg-primary text-white p-2 shadow-md active:scale-95"
              >
                <AiOutlineMinus />
              </button>
              <button
                onClick={incrementWeight}
                className="rounded-full bg-primary text-white p-2 shadow-md active:scale-95"
              >
                <AiOutlinePlus />
              </button>
            </div>
          </div>
        </div>

        {/* Consent Section */}
        <PatientConsent
          formData={consentData}
          setFormData={setConsentData}
          isConsentAcepted={consent}
          setConsentAccepted={setConsent}
          isUnder18={isUnder18}
        />
      </div>

      <div className="sticky bottom-0 bg-white z-10 w-full pt-4 mt-6 border-t border-gray-100">
        {/* Action Buttons */}
        <div className="flex space-x-3 pb-2 ">
          <button
            className="bg-gray-200 text-gray-700 font-bold py-3 rounded-full w-full"
            onClick={() => navigate(-1)}
          >
            Back
          </button>
          <button
            className={`${isProcessing ? "bg-gray-400" : "bg-primary"} text-white font-bold py-3 rounded-full w-full transition-colors`}
            onClick={handleContinue}
            disabled={isProcessing}
          >
            {isProcessing ? "Processing..." : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OtherDetailsPage;
